import { PARAMS } from "@src/common/constants";
import { createDemoBoardMutationFn } from "@src/components/board/mutations/create-demo-board";

const DEMO_BOARD_URL_BY_SESSION_STORAGE_KEY =
	"illumi-demo-board-url-by-session";

async function calculateAnonymousSessionId(url: URL): Promise<string> {
	const fingerprint = {
		hostname: url.hostname,
		userAgent: navigator.userAgent,
		language: navigator.language,
		languages: navigator.languages,
		platform: navigator.platform,
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		hardwareConcurrency: navigator.hardwareConcurrency,
		deviceMemory:
			"deviceMemory" in navigator ? navigator.deviceMemory : undefined,
		maxTouchPoints: navigator.maxTouchPoints,
		screen: {
			width: window.screen.width,
			height: window.screen.height,
			colorDepth: window.screen.colorDepth,
			pixelDepth: window.screen.pixelDepth,
		},
	};
	const input = JSON.stringify(fingerprint);

	if (window.crypto?.subtle) {
		const bytes = new TextEncoder().encode(input);
		const hashBuffer = await window.crypto.subtle.digest("SHA-256", bytes);
		const hash = Array.from(new Uint8Array(hashBuffer))
			.map((byte) => byte.toString(16).padStart(2, "0"))
			.join("");
		return `anon-${hash.slice(0, 32)}`;
	}

	let fallbackHash = 0;
	for (let index = 0; index < input.length; index += 1) {
		fallbackHash = (fallbackHash << 5) - fallbackHash + input.charCodeAt(index);
		fallbackHash |= 0;
	}

	return `anon-${Math.abs(fallbackHash).toString(16)}`;
}

function getStoredDemoBoardUrl(sessionId: string): string | null {
	const raw = sessionStorage.getItem(DEMO_BOARD_URL_BY_SESSION_STORAGE_KEY);
	if (!raw) return null;

	try {
		const parsed = JSON.parse(raw) as Record<string, string>;
		return parsed[sessionId] ?? null;
	} catch {
		sessionStorage.removeItem(DEMO_BOARD_URL_BY_SESSION_STORAGE_KEY);
		return null;
	}
}

function saveStoredDemoBoardUrl(sessionId: string, boardUrl: string): void {
	const raw = sessionStorage.getItem(DEMO_BOARD_URL_BY_SESSION_STORAGE_KEY);
	let parsed: Record<string, string> = {};

	if (raw) {
		try {
			parsed = JSON.parse(raw) as Record<string, string>;
		} catch {
			parsed = {};
		}
	}

	parsed[sessionId] = boardUrl;
	sessionStorage.setItem(
		DEMO_BOARD_URL_BY_SESSION_STORAGE_KEY,
		JSON.stringify(parsed),
	);
}

function isPreviewHostname(hostname: string): boolean {
	return (
		/\.preview\.illumi\.systems$/i.test(hostname) ||
		/\.proto-webapp\.pages\.dev$/i.test(hostname) ||
		/\.preview\.illumi\.sh$/i.test(hostname)
	);
}

function isLocalhostHostname(hostname: string): boolean {
	return hostname === "localhost" || hostname === "127.0.0.1";
}

function rewriteWhiteboardUrlToCurrentHost(
	whiteboardUrl: string,
	currentUrl: URL,
): string {
	const rewritten = new URL(whiteboardUrl);
	rewritten.protocol = currentUrl.protocol;
	rewritten.host = currentUrl.host;
	return rewritten.toString();
}

function applyDemoWhiteboardUrlRewrites(
	whiteboardUrl: string,
	currentUrl: URL,
): string {
	let out = whiteboardUrl;
	if (
		isPreviewHostname(currentUrl.hostname) ||
		isLocalhostHostname(currentUrl.hostname)
	) {
		out = rewriteWhiteboardUrlToCurrentHost(out, currentUrl);
	}
	return out;
}

export async function runDemoFlow(url: URL): Promise<string | null> {
	const sessionId = await calculateAnonymousSessionId(url);
	const storedUrl = getStoredDemoBoardUrl(sessionId);
	if (storedUrl) {
		return applyDemoWhiteboardUrlRewrites(
			storedUrl,
			new URL(window.location.href),
		);
	}

	const result = await createDemoBoardMutationFn(
		sessionId,
		url.searchParams.get(PARAMS.demo) ?? "",
	);
	if (!result?.whiteboardUrl) {
		return null;
	}

	saveStoredDemoBoardUrl(sessionId, result.whiteboardUrl);
	return applyDemoWhiteboardUrlRewrites(
		result.whiteboardUrl,
		new URL(window.location.href),
	);
}
